import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

// Renderer for professional bios. Whitelist is deliberately narrow — bios get
// paragraph breaks, sub-headers (one level), inline emphasis, links, and lists.
// No images, no raw HTML (react-markdown drops raw HTML by default unless
// rehype-raw is added — don't add it), no blockquotes, no tables, no code.
//
// All heading levels collapse to a styled <h3> so:
//   1. Visual hierarchy can't outrank the page's own <h2> "Professional Bio"
//      section label (keeps the document outline correct for screen readers
//      and search engines).
//   2. Pros writing `# Headline` and `### Headline` get the same result —
//      no surprise jumps between giant and tiny text.
//
// Two presentation variants:
//   - "profile": navy, font-normal, text-lg (public profile body copy)
//   - "compact": gray-600, text-sm (pro dashboard preview, admin views)

type Variant = "profile" | "compact";

const variantStyles: Record<
  Variant,
  {
    wrapper: string;
    p: string;
    pSpacing: string;
    h3: string;
    h3Spacing: string;
    a: string;
    list: string;
    li: string;
  }
> = {
  profile: {
    wrapper: "text-lg font-normal leading-[1.7] text-navy",
    p: "",
    pSpacing: "mt-13",
    h3: "font-semibold text-navy",
    h3Spacing: "mt-21",
    a: "underline decoration-gold decoration-2 underline-offset-4 hover:decoration-navy focus-visible:decoration-navy",
    list: "list-disc pl-21",
    li: "mt-5 first:mt-0",
  },
  compact: {
    wrapper: "text-sm leading-relaxed text-gray-600",
    p: "",
    pSpacing: "mt-13",
    h3: "font-semibold text-navy",
    h3Spacing: "mt-13",
    a: "underline decoration-gold underline-offset-2 hover:decoration-navy focus-visible:decoration-navy",
    list: "list-disc pl-21",
    li: "mt-3 first:mt-0",
  },
};

export default function MarkdownBio({
  markdown,
  variant = "profile",
  className = "",
}: {
  markdown: string;
  variant?: Variant;
  className?: string;
}) {
  const s = variantStyles[variant];
  // Wrap each child so the FIRST block has no top margin and SUBSEQUENT blocks
  // get spacing — matches the previous `mt-13` pattern (i > 0 ? "mt-13" : "").
  // react-markdown doesn't expose sibling index in `components`, so we use the
  // tailwind sibling selector pattern instead: every child gets the spacing
  // class, and we strip it from the first via `[&>*:first-child]:mt-0`.
  return (
    <div
      className={`${s.wrapper} [&>*:first-child]:mt-0 ${className}`.trim()}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        // Collapse every heading level to a styled h3.
        allowedElements={[
          "p",
          "h1",
          "h2",
          "h3",
          "h4",
          "h5",
          "h6",
          "strong",
          "em",
          "a",
          "ul",
          "ol",
          "li",
          "br",
        ]}
        // Anything outside the allowlist — images, blockquotes, code blocks,
        // tables, raw HTML — silently has its wrapper dropped while text
        // children survive as plain text. Better than a noisy error.
        unwrapDisallowed
        components={{
          p: ({ children }) => (
            <p className={`${s.pSpacing} ${s.p}`.trim()}>{children}</p>
          ),
          h1: ({ children }) => (
            <h3 className={`${s.h3Spacing} ${s.h3}`.trim()}>{children}</h3>
          ),
          h2: ({ children }) => (
            <h3 className={`${s.h3Spacing} ${s.h3}`.trim()}>{children}</h3>
          ),
          h3: ({ children }) => (
            <h3 className={`${s.h3Spacing} ${s.h3}`.trim()}>{children}</h3>
          ),
          h4: ({ children }) => (
            <h3 className={`${s.h3Spacing} ${s.h3}`.trim()}>{children}</h3>
          ),
          h5: ({ children }) => (
            <h3 className={`${s.h3Spacing} ${s.h3}`.trim()}>{children}</h3>
          ),
          h6: ({ children }) => (
            <h3 className={`${s.h3Spacing} ${s.h3}`.trim()}>{children}</h3>
          ),
          a: ({ href, children }) => (
            <a
              href={href}
              className={s.a}
              target="_blank"
              rel="noopener noreferrer nofollow"
            >
              {children}
            </a>
          ),
          ul: ({ children }) => (
            <ul className={`${s.pSpacing} ${s.list}`.trim()}>{children}</ul>
          ),
          ol: ({ children }) => (
            <ol
              className={`${s.pSpacing} ${s.list.replace("list-disc", "list-decimal")}`.trim()}
            >
              {children}
            </ol>
          ),
          li: ({ children }) => <li className={s.li}>{children}</li>,
        }}
      >
        {markdown}
      </ReactMarkdown>
    </div>
  );
}
